/** Com'è finita una pratica, e perché — in cima al dettaglio.
 *
 * Chi apre una pratica chiusa vuole sapere prima l'esito e poi il contenuto. Oggi
 * il dettaglio RDI non lo dice affatto: `Review3.tsx` non contiene la stringa
 * `discarded`, e `error_message` (che porta la nota di scarto) arriva nel payload
 * senza essere mai renderizzato — una pratica scartata si apre identica a una
 * ancora da lavorare.
 *
 * **La domanda che questo riquadro deve rispondere è "perché è stata scartata".**
 * Lo scarto ha due origini e va detto quale, perché si rimediano in modi opposti:
 * l'operatore scrive sempre una nota (183 pratiche su 183) e ha un nome; la
 * pipeline non lascia **alcuna** riga di audit e il suo motivo va letto dalla
 * classificazione (55 pratiche). Se le due si mescolassero, un quarto degli scarti
 * sembrerebbe privo di spiegazione quando invece ce l'ha.
 *
 * Per l'approvazione basta una riga: *chi*, *quando*, *cosa è stato cambiato*. Un
 * "perché approvata" non viene mostrato perché **non è un dato che esiste** —
 * l'approvazione non chiede una motivazione (`ApproveBody.note` ha default `""` e
 * il frontend passa stringa vuota: `details.note` è vuoto in 262 approvazioni su
 * 285, misurato il 2026-08-04). Mostrare al suo posto `classification.reason`
 * sarebbe peggio del silenzio: sulle approvate è la frase fissa "Classificato da
 * pipeline V3 agentica LangGraph", identica su tutte e 281.
 */

import type { AuditEntry } from "../api/types";

import { estraiCambiamenti, formatActivityDate } from "./ActivityPanel";
import { rejectCategoryLabel } from "./activityLabels";

export interface PipelineDiscard {
  key: string;
  label: string;
  detail?: string | null;
  recognised: boolean;
}

/** L'ultima riga di audit di un'azione, o `undefined`.
 *
 * `/audit/by-request` ordina `created_at DESC`, quindi la prima è la più recente:
 * conta, perché una pratica riaperta e riapprovata ha due `approve` e l'esito
 * corrente è il secondo.
 */
function ultima(entries: AuditEntry[], action: string): AuditEntry | undefined {
  return entries.find((e) => e.action === action);
}

/** Gli eventi del ciclo corrente: dal più recente fino alla riaperture precedente.
 *
 * Una pratica riaperta e rilavorata ha due giri di correzioni, e sommarli
 * direbbe che sono stati corretti campi che nel giro attuale nessuno ha toccato.
 * Le righe arrivano ordinate `created_at DESC`, quindi il ciclo corrente è il
 * prefisso che precede il primo `reopen` incontrato.
 */
function cicloCorrente(entries: AuditEntry[]): AuditEntry[] {
  const i = entries.findIndex((e) => e.action === "reopen");
  return i === -1 ? entries : entries.slice(0, i);
}

function Riga({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: "var(--font-size-sm)", marginTop: "var(--space-1)" }}>{children}</div>;
}

export function PracticeOutcome({
  status,
  entries,
  pipelineDiscard,
  notaFallback,
}: {
  status: string;
  entries: AuditEntry[];
  pipelineDiscard?: PipelineDiscard | null;
  /** Nota da usare se l'audit non ce l'ha. Su una scartata è `error_message`, dove
   *  il reject duplica la nota dell'operatore (`requests.py`, SET error_message =
   *  body.note): serve solo se la riga di audit manca, perché quella è la fonte
   *  che porta anche il nome e la data. */
  notaFallback?: string | null;
}) {
  const scartata = status === "discarded";
  const approvata = status === "done" || status === "auto_approved";
  if (!scartata && !approvata) return null;

  const evento = scartata ? ultima(entries, "reject") : ultima(entries, "approve");
  const chi = evento?.operator_name || evento?.operator_id || null;
  const quando = evento ? formatActivityDate(evento.created_at) : null;

  // Le modifiche non stanno tutte nella riga di approvazione: le correzioni delle
  // entità sono righe `correction` a sé, scritte prima. Leggendo solo `approve` il
  // riassunto ne contava una quando ce n'erano due — visto sulla pagina il
  // 2026-08-04 su una pratica che mostrava "1 modifica" con una correzione di
  // entità nella cronologia sotto.
  //
  // Dedotto per campo tenendo l'evento più recente: un campo corretto e poi
  // imposto a mano è una modifica, non due.
  const perCampo = new Map<string, ReturnType<typeof estraiCambiamenti>[number]>();
  for (const e of cicloCorrente(entries)) {
    if (e.action !== "approve" && e.action !== "correction") continue;
    for (const c of estraiCambiamenti(e)) {
      if (!perCampo.has(c.campo)) perCampo.set(c.campo, c);
    }
  }
  const cambiamenti = [...perCampo.values()];
  const categorie = evento?.feedback_categories ?? [];
  const nota = evento?.feedback_note || notaFallback || null;

  const colore = scartata ? "var(--indaco-danger)" : "var(--indaco-success)";
  const icona = scartata ? "cancel" : "check_circle";

  // Scarto della pipeline: nessun operatore, nessuna riga di audit. Va detto che
  // non c'è stato un umano, altrimenti "Scartata" senza nome si legge come un dato
  // mancante invece che come una decisione automatica.
  const automatico = scartata && !evento;

  let titolo: string;
  if (approvata) {
    titolo = status === "auto_approved" ? "Approvata automaticamente" : "Approvata";
  } else {
    titolo = automatico ? "Scartata dal sistema, senza operatore" : "Scartata";
  }

  return (
    <div
      className="card"
      style={{ padding: "var(--space-3)", borderLeft: `4px solid ${colore}` }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: colore }} aria-hidden>
          {icona}
        </span>
        <span className="fw-semibold" style={{ color: colore }}>{titolo}</span>
        {chi && <span className="text-muted text-sm">da {chi}</span>}
        {quando && (
          <span className="text-muted text-sm" style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>
            {quando}
          </span>
        )}
      </div>

      {/* Perché: la parte che oggi manca del tutto. */}
      {automatico && pipelineDiscard && (
        <Riga>
          <span className="text-muted fw-medium">Motivo: </span>
          <span className="fw-medium">{pipelineDiscard.label}</span>
          {pipelineDiscard.detail && (
            <details style={{ marginTop: 2 }}>
              <summary className="text-muted text-xs" style={{ cursor: "pointer" }}>
                segnali che hanno fatto scattare il filtro
              </summary>
              <div
                className="text-muted text-xs"
                style={{ fontFamily: "var(--font-family-mono)", whiteSpace: "pre-wrap", marginTop: 2 }}
              >
                {pipelineDiscard.detail}
              </div>
            </details>
          )}
        </Riga>
      )}

      {automatico && !pipelineDiscard && (
        <Riga>
          <span className="text-muted">
            Il motivo dello scarto non è stato registrato su questa pratica.
          </span>
        </Riga>
      )}

      {categorie.length > 0 && (
        <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap", marginTop: "var(--space-2)" }}>
          {categorie.map((k) => (
            <span key={k} className="badge badge-neutral badge-sm">
              {rejectCategoryLabel(k)}
            </span>
          ))}
        </div>
      )}

      {nota && (
        <Riga>
          <span className="text-muted fw-medium">Nota: </span>
          {nota}
        </Riga>
      )}

      {/* Cosa è stato cambiato prima di chiudere. Il conteggio a zero è
          un'informazione, non un vuoto: significa che il sistema aveva ragione. */}
      {approvata && (
        <Riga>
          {cambiamenti.length === 0 ? (
            <span className="text-muted">Approvata senza modifiche: nessun campo è stato corretto.</span>
          ) : (
            <>
              <span className="text-muted fw-medium">
                {cambiamenti.length === 1 ? "1 modifica" : `${cambiamenti.length} modifiche`}:{" "}
              </span>
              {cambiamenti.map((c) => c.campo).join(", ")}
              <span className="text-muted text-xs"> — il dettaglio in fondo alla pagina</span>
            </>
          )}
        </Riga>
      )}

      {/* Una pratica chiusa senza riga di audit non è normale: dirlo evita che il
          riquadro sembri semplicemente incompleto. */}
      {approvata && !evento && (
        <Riga>
          <span className="text-muted">
            Nessuna registrazione di chi l'ha approvata: la pratica risulta chiusa senza un
            intervento tracciato.
          </span>
        </Riga>
      )}
    </div>
  );
}
